import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { ApiErrorDialogService } from './api-error-dialog.service';

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private toastContainerId = 'toast-container';
  private toastLayerId = 'sgw-toast-layer';
  private readonly toastZIndex = '2147483647';
  private readonly document = inject(DOCUMENT);

  showToast(message: string, type: 'success' | 'error' | 'info' = 'info', duration: number = 5000, customBgColor?: string): void {
    const toastContainer = this.getToastContainer();

    if (!toastContainer) {
      console.error('Toast container not found!');
      return;
    }

    // Mapear el tipo a la clase de Bootstrap, o usar color personalizado
    let typeClass = '';
    if (customBgColor) {
      typeClass = ''; // No usar clase de Bootstrap si hay color personalizado
    } else {
      typeClass = type === 'error' ? 'text-bg-danger' : `text-bg-${type}`;
    }

    // Crear el elemento del toast
    const toastElement = document.createElement('div');
    toastElement.className = `toast align-items-center ${typeClass} border-0`;
    toastElement.setAttribute('role', 'alert');
    toastElement.setAttribute('aria-live', 'assertive');
    toastElement.setAttribute('aria-atomic', 'true');

    // Aplicar color personalizado si se proporciona
    if (customBgColor) {
      toastElement.style.backgroundColor = customBgColor;
      toastElement.style.color = 'var(--ui-on-primary)';
    }

    const content = this.document.createElement('div');
    content.className = 'd-flex';
    const body = this.document.createElement('div');
    body.className = 'toast-body';
    body.textContent = message;
    const close = this.document.createElement('button');
    close.type = 'button';
    close.className = 'btn-close btn-close-white me-2 m-auto';
    close.setAttribute('data-bs-dismiss', 'toast');
    close.setAttribute('aria-label', 'Close');
    content.append(body, close);
    toastElement.appendChild(content);

    // Agregar el toast al contenedor
    toastContainer.appendChild(toastElement);

    // Inicializar el toast con Bootstrap
    const toast = new (window as any).bootstrap.Toast(toastElement, { delay: duration });
    toast.show();

    // Eliminar el toast del DOM después de que desaparezca
    toastElement.addEventListener('hidden.bs.toast', () => {
      toastElement.remove();
    });
  }

  /**
   * Mostrar notificación interna con el acento semántico de marca.
   */
  showInternalToast(message: string, duration: number = 5000): void {
    this.showToast(message, 'info', duration, 'var(--ui-primary)');
  }

  private getToastContainer(): HTMLElement | null {
    let toastContainer = this.document.getElementById(this.toastContainerId);

    if (!toastContainer) {
      toastContainer = this.document.createElement('div');
      toastContainer.id = this.toastContainerId;
      toastContainer.className = 'toast-container position-fixed bottom-0 end-0 p-3';
    }

    // Keep notifications outside Angular Material's overlay tree. This avoids
    // inheriting any stacking context or backdrop-filter from dialogs/backdrops.
    let toastLayer = this.document.getElementById(this.toastLayerId);
    if (!toastLayer) {
      toastLayer = this.document.createElement('div');
      toastLayer.id = this.toastLayerId;
      toastLayer.className = 'sgw-toast-layer';
    }

    // Reinsertar la capa incluso cuando ya pertenece al body la mueve al
    // final del stacking order del documento, por delante del overlay de CDK.
    this.document.body.appendChild(toastLayer);

    if (toastContainer.parentElement !== toastLayer) {
      toastLayer.appendChild(toastContainer);
    }

    toastLayer.style.setProperty('z-index', this.toastZIndex, 'important');
    toastContainer.style.setProperty('z-index', this.toastZIndex, 'important');

    return toastContainer;
  }
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  constructor(
    private readonly toastService: ToastService,
    private readonly apiErrorDialogService: ApiErrorDialogService,
  ) {}

  notify(
    message: unknown,
    type: 'success' | 'error' | 'info' = 'info',
    duration = 5000,
    customBgColor?: string,
  ): void {
    if (type === 'error') {
      if (typeof message === 'string') {
        this.apiErrorDialogService.openErrorMessage(message);
      } else {
        this.apiErrorDialogService.openFromError(message, 'No se pudo completar la acción');
      }
      return;
    }
    this.toastService.showToast(String(message ?? ''), type, duration, customBgColor);
  }

  showInternalToast(message: string, duration = 5000): void {
    this.toastService.showInternalToast(message, duration);
  }
}
